// Spodní lišta na telefonu (krok 13.7a, návrh docs/predani/2026-09-28-telefon-navrh.md § 5.1): pět míst — Domů,
// tři sekce a Menu, kam dosáhne palec. Sekce: nejdřív oblíbené uživatele (★ v menu, totéž co nahoře v menu a na
// Domů), zbytek výchozí podle role; jen sekce z menu (= co server povolil v GET /ja), bez opakování. Menu otevře
// vysouvací menu (Lista) se všemi sekcemi. Shell ji vykresluje na telefonu a na dotykovém tabletu na výšku
// (`useMaSpodniListu`; tablet od 11. 10. 2026, rozhodnutí 05/2940 a, W51).
import type { Ja } from '@ecoser/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { IKONA_SEKCE, Ikona, IkonaSekce } from './ikony';
import { type Menu, type PolozkaMenu, vsechnyPolozky } from './menu';
import { useNastaveni } from './nastaveni-uzivatele';
import { PocetSvozu } from './PocetSvozu';
import './Shell.css';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';

// Počet „Ke kontrole“ čte čtyři fronty (api/ke-kontrole.ts) — kód se stáhne až po startu; odznak se ukazuje až
// s načtenými počty, takže se nic neposune (rozpočet hlavního balíku).
const PocetKeKontrole = lina<{ testId?: string }>(() =>
  import('./PocetKeKontrole').then((m) => m.PocetKeKontrole),
);

/** Kolik sekcí je ve spodní liště mezi Domů a Menu. */
export const POCET_SEKCI_SPODNI_LISTY = 3;

/**
 * Výchozí sekce spodní lišty podle role (návrh § 5.1) — jen pořadí zobrazení, co role smí, rozhoduje server.
 * Oblíbené uživatele mají přednost; nepovolenou sekci nahradí Docházka, Kalendář a pak pořadí menu.
 */
export const VYCHOZI_SEKCE_ROLE: Readonly<Record<string, readonly string[]>> = {
  ridic: ['muj_den', 'dochazka', 'kalendar'],
  koordinator: ['muj_den', 'zakazky', 'ke_kontrole'],
  skladnik: ['muj_den', 'vazeni', 'dochazka'],
  mlynar: ['sklad_evidence', 'dochazka', 'kalendar'],
  auditor: ['muj_den', 'audity', 'dochazka'],
  vedouci_auditor: ['muj_den', 'audity', 'dochazka'],
  dispecer: ['zakazky', 'kalendar', 'ke_kontrole'],
  admin: ['zakazky', 'kalendar', 'ke_kontrole'],
  jednatel: ['zakazky', 'kalendar', 'ke_kontrole'],
  obchodnik: ['moje_firmy', 'interakce', 'kalendar'],
  vedouci_obchodu: ['moje_firmy', 'interakce', 'kalendar'],
  ucetni: ['ucetnictvi', 'pohledavky', 'dochazka'],
  administrativa: ['kalendar', 'dochazka', 'iso'],
};

const NAHRADNI_SEKCE = ['dochazka', 'kalendar'];

/** Krátké popisky dlouhých názvů (celý název nese `aria-label`). */
const KRATKE_NAZVY: Readonly<Record<string, string>> = {
  svoz_pozadavky: 'Svozy',
  'sklad_evidence:evidence': 'Evidence',
  hlaseni: 'Hlášení',
  archiv_dokumentu: 'Archiv',
  vzory_dokumentu: 'Vzory',
  sklad: 'Katalog',
  znalostni_baze: 'Znalosti',
  celni_sprava: 'Celní',
  interni_cenik: 'Ceník',
};

/** Popisek do spodní lišty: krátký pro dlouhé názvy (i ty, které závisí na roli — mlynář, ISŘ). */
export function kratkyNazev(polozka: PolozkaMenu): string {
  if (polozka.klic === 'iso') return polozka.nazev === 'Moje školení a hlášení' ? 'Školení' : 'ISŘ';
  if (polozka.klic === 'sklad_evidence' && polozka.nazev === 'Drcení a štítky') return 'Drcení';
  return KRATKE_NAZVY[polozka.klic] ?? polozka.nazev;
}

/** Tři sekce spodní lišty: oblíbené, výchozí role, náhradní, pořadí menu — bez Domů a bez opakování. */
export function sekceSpodniListy(
  menu: Menu,
  ja: Pick<Ja, 'role' | 'role_efektivni'>,
  oblibene: readonly string[],
): PolozkaMenu[] {
  const polozky = vsechnyPolozky(menu).filter((p) => p.klic !== 'home');
  const podleKlice = new Map(polozky.map((p) => [p.klic, p]));
  const role = ja.role_efektivni || ja.role || '';
  const poradi = [
    ...oblibene,
    ...(VYCHOZI_SEKCE_ROLE[role] ?? []),
    ...NAHRADNI_SEKCE,
    ...polozky.map((p) => p.klic),
  ];
  const vybrane: PolozkaMenu[] = [];
  for (const klic of poradi) {
    const polozka = podleKlice.get(klic);
    if (polozka && !vybrane.includes(polozka)) vybrane.push(polozka);
    if (vybrane.length === POCET_SEKCI_SPODNI_LISTY) break;
  }
  return vybrane;
}

function MistoListy({
  cesta,
  nazev,
  popisek,
  aktivni,
  ikona,
  pocet,
  klic,
}: {
  cesta: string;
  nazev: string;
  popisek: string;
  aktivni: boolean;
  ikona: ReactNode;
  pocet?: ReactNode;
  klic: string;
}) {
  return (
    <li className="spodni-lista__polozka" data-misto={klic}>
      <Link
        to={cesta}
        className={`spodni-lista__misto${aktivni ? ' spodni-lista__misto--aktivni' : ''}`}
        aria-current={aktivni ? 'page' : undefined}
        // Krátký popisek je vidět, celý název slyší čtečka — viditelný text je na začátku (hlasové ovládání).
        aria-label={popisek === nazev ? undefined : `${popisek} — ${nazev}`}
      >
        <span className="spodni-lista__ikona" aria-hidden="true">
          {ikona}
        </span>
        {pocet}
        <span className="spodni-lista__popisek">{popisek}</span>
      </Link>
    </li>
  );
}

interface Props {
  ja: Ja;
  menu: Menu;
  /** Klíč aktivní sekce (z cesty); Domů = `home`. */
  aktivniKlic?: string;
  menuOtevrene: boolean;
  naMenu: () => void;
}

export function SpodniLista({ ja, menu, aktivniKlic, menuOtevrene, naMenu }: Props) {
  const { nastaveni } = useNastaveni();
  const sekce = sekceSpodniListy(menu, ja, nastaveni.quick_links);
  return (
    <nav className="spodni-lista" aria-label="Rychlá navigace" data-testid="spodni-lista">
      <ul className="spodni-lista__seznam">
        <MistoListy
          klic="home"
          cesta={menu.domu.cesta}
          nazev={menu.domu.nazev}
          popisek={menu.domu.nazev}
          aktivni={aktivniKlic === 'home' && !menuOtevrene}
          ikona={<Ikona id="home" velikost={20} />}
        />
        {sekce.map((polozka) => (
          <MistoListy
            key={polozka.klic}
            klic={polozka.klic}
            cesta={polozka.cesta}
            nazev={polozka.nazev}
            popisek={kratkyNazev(polozka)}
            aktivni={polozka.klic === aktivniKlic && !menuOtevrene}
            ikona={
              IKONA_SEKCE[polozka.klic] ? (
                <IkonaSekce klic={polozka.klic} velikost={20} />
              ) : (
                polozka.nazev.charAt(0).toUpperCase()
              )
            }
            pocet={
              polozka.klic === 'ke_kontrole' ? (
                <NacitaniCasti tiche>
                  <PocetKeKontrole testId="spodni-pocet-ke-kontrole" />
                </NacitaniCasti>
              ) : polozka.klic === 'svoz_pozadavky' ? (
                <PocetSvozu testId="spodni-pocet-svozu" />
              ) : null
            }
          />
        ))}
        <li className="spodni-lista__polozka" data-misto="menu">
          <button
            type="button"
            className={`spodni-lista__misto${menuOtevrene ? ' spodni-lista__misto--aktivni' : ''}`}
            aria-expanded={menuOtevrene}
            aria-label={menuOtevrene ? 'Zavřít menu' : 'Menu — všechny sekce'}
            onClick={naMenu}
          >
            <span className="spodni-lista__ikona" aria-hidden="true">
              <Ikona id={menuOtevrene ? 'x' : 'menu'} velikost={20} />
            </span>
            <span className="spodni-lista__popisek" aria-hidden="true">
              {menuOtevrene ? 'Zavřít' : 'Menu'}
            </span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
